import { useCallback, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { SessionLobby } from "./components/SessionLobby";
import { AgentMesh } from "./components/AgentMesh";
import { MemoryPanel } from "./components/MemoryPanel";
import { PrivacyAudit } from "./components/PrivacyAudit";
import { NavBar, type SitePage } from "./components/NavBar";
import { FeaturesPage } from "./components/FeaturesPage";
import { PricingPage } from "./components/PricingPage";
import { AboutPage } from "./components/about/AboutPage";
import { DocsPage } from "./components/DocsPage";
import { MarketplacePage } from "./components/marketplace";
import { Footer } from "./components/Footer";
import { CustomCursor } from "./components/CustomCursor";
import { useWebSocket } from "./hooks/useWebSocket";
import type {
  AgentNode,
  AgentThread,
  ConnectionStatus,
  ConsoleView,
  MemoryItem,
  PrivacyMode,
  ServerMessage,
  Session,
  Task,
} from "./types";
import { PRIVACY_DEFAULTS } from "./types";

// =============================================================================
// Connection Status Badge
// =============================================================================

function ConnectionBadge({ status }: { status: ConnectionStatus }) {
  const config: Record<
    ConnectionStatus,
    { color: string; label: string; pulse: boolean }
  > = {
    disconnected: { color: "bg-hvme-text-muted", label: "Disconnected", pulse: false },
    connecting: { color: "bg-hvme-gold", label: "Connecting", pulse: true },
    connected: { color: "bg-hvme-green", label: "Connected", pulse: false },
    reconnecting: { color: "bg-hvme-gold", label: "Reconnecting", pulse: true },
    error: { color: "bg-hvme-red", label: "Error", pulse: false },
  };

  const { color, label, pulse } = config[status];

  return (
    <div className="flex items-center gap-2 text-xs text-hvme-text-secondary">
      <span className="relative flex h-2 w-2">
        {pulse && (
          <span
            className={`absolute inline-flex h-full w-full animate-ping rounded-full ${color} opacity-75`}
          />
        )}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${color}`} />
      </span>
      {label}
    </div>
  );
}

// =============================================================================
// Console Navigation (internal tabs for console views)
// =============================================================================

const CONSOLE_NAV_ITEMS: { view: ConsoleView; label: string; requiresSession: boolean }[] = [
  { view: "lobby", label: "Session", requiresSession: false },
  { view: "orchestration", label: "Agents", requiresSession: true },
  { view: "memory", label: "Memory", requiresSession: true },
  { view: "privacy", label: "Privacy", requiresSession: true },
];

function ConsoleNavBar({
  currentView,
  onNavigate,
  hasSession,
}: {
  currentView: ConsoleView;
  onNavigate: (view: ConsoleView) => void;
  hasSession: boolean;
}) {
  return (
    <nav className="flex items-center gap-1">
      {CONSOLE_NAV_ITEMS.map(({ view, label, requiresSession }) => {
        const disabled = requiresSession && !hasSession;
        const active = currentView === view;

        return (
          <button
            key={view}
            onClick={() => !disabled && onNavigate(view)}
            disabled={disabled}
            className={`
              relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300
              ${active
                ? "text-hvme-primary"
                : disabled
                  ? "text-hvme-text-muted cursor-not-allowed"
                  : "text-hvme-text-secondary hover:text-hvme-text-primary"
              }
            `}
          >
            {label}
            {active && (
              <motion.div
                layoutId="console-nav-indicator"
                className="absolute bottom-0 left-2 right-2 h-px bg-hvme-primary"
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              />
            )}
          </button>
        );
      })}
    </nav>
  );
}

// =============================================================================
// Main App
// =============================================================================

export function App() {
  // Site page state (for main nav)
  const [currentPage, setCurrentPage] = useState<SitePage>("console");

  // Console view state (for console sub-nav)
  const [currentView, setCurrentView] = useState<ConsoleView>("lobby");

  // Session state
  const [session, setSession] = useState<Session | null>(null);

  // Agent mesh state
  const [agents, setAgents] = useState<AgentNode[]>([]);
  const [threads, setThreads] = useState<AgentThread[]>([]);

  // Task state
  const [tasks, setTasks] = useState<Task[]>([]);

  // Memory state
  const [memoryItems, setMemoryItems] = useState<MemoryItem[]>([]);

  // Handle incoming WS messages
  const handleMessage = useCallback((message: ServerMessage) => {
    switch (message.type) {
      case "session.ready": {
        // Gateway sends session.ready with sessionId in payload
        const payload = (message as unknown as { payload: { sessionId: string; token: string; expiresAt: string; capabilities: string[] } }).payload;
        console.log("[HVME] Session ready from gateway:", payload.sessionId);
        setSession((prev) =>
          prev
            ? { ...prev, id: payload.sessionId, status: "ready" }
            : {
                id: payload.sessionId,
                privacyMode: "standard",
                memoryPolicy: PRIVACY_DEFAULTS.standard,
                startedAt: Date.now(),
                status: "ready",
              },
        );
        break;
      }

      case "presence.update":
        setAgents(message.agents);
        setThreads(message.threads);
        break;

      case "task.accepted":
        setTasks((prev) => [...prev, message.task]);
        break;

      case "task.progress":
        setTasks((prev) =>
          prev.map((t) =>
            t.id === message.taskId
              ? { ...t, progress: message.progress, state: message.state }
              : t,
          ),
        );
        break;

      case "task.result":
        setTasks((prev) =>
          prev.map((t) =>
            t.id === message.taskId
              ? { ...t, result: message.result, state: "complete", completedAt: Date.now() }
              : t,
          ),
        );
        break;

      case "task.error":
        setTasks((prev) =>
          prev.map((t) =>
            t.id === message.taskId
              ? { ...t, error: message.error, state: "failed", completedAt: Date.now() }
              : t,
          ),
        );
        break;

      case "mem.update":
        setMemoryItems(message.items);
        break;

      case "mem.ttl":
        setMemoryItems((prev) =>
          prev.map((m) =>
            m.id === message.memoryId
              ? { ...m, remainingSeconds: message.remainingSeconds }
              : m,
          ),
        );
        break;

      case "mem.expired":
        setMemoryItems((prev) =>
          prev.map((m) =>
            m.id === message.memoryId
              ? { ...m, remainingSeconds: 0 }
              : m,
          ),
        );
        // Remove after fade-out animation completes
        setTimeout(() => {
          setMemoryItems((prev) => prev.filter((m) => m.id !== message.memoryId));
        }, 600);
        break;

      case "error":
        console.error(`[HVME] Server error: ${message.code} - ${message.message}`);
        break;
    }
  }, []);

  // WebSocket connection (don't auto-connect — session starts it)
  const { status, send, connect, disconnect } = useWebSocket({
    onMessage: handleMessage,
    autoReconnect: false,
  });

  // Site navigation handler
  const handlePageNavigate = (page: SitePage) => {
    setCurrentPage(page);
    // When navigating to console, show the lobby if no session
    if (page === "console" && !session) {
      setCurrentView("lobby");
    }
  };

  // Session handlers
  const handleStartSession = useCallback(
    (privacyMode: PrivacyMode) => {
      console.log("[HVME] Starting session with mode:", privacyMode);

      // Store privacy mode for when session.ready arrives
      const pendingSession: Session = {
        id: "", // Will be set by gateway
        privacyMode,
        memoryPolicy: PRIVACY_DEFAULTS[privacyMode],
        startedAt: Date.now(),
        status: "initializing",
      };
      setSession(pendingSession);
      setCurrentView("orchestration");

      // Connect WebSocket and send session.create
      connect();

      // Send session.create after connection (with small delay for connection to establish)
      setTimeout(() => {
        send({
          type: "session.create",
          payload: {
            label: `console-${Date.now()}`,
            capabilities: ["task.submit", "mem.write", "mem.pin"],
          },
          requestId: `create-${Date.now()}`,
        });
        console.log("[HVME] Sent session.create to gateway");
      }, 500);
    },
    [connect, send],
  );

  const handleSubmitTask = useCallback(
    (description: string) => {
      // Create local task for immediate feedback
      const taskId = crypto.randomUUID();
      const task: Task = {
        id: taskId,
        description,
        state: "pending",
        progress: 0,
        submittedAt: Date.now(),
      };
      setTasks((prev) => [...prev, task]);

      // Send in gateway wire format with encrypted envelope prefix
      // In production, this would use actual FHE encryption
      const encryptedData = `enc:${btoa(JSON.stringify({ description }))}`;
      send({
        type: "task.submit",
        payload: {
          kind: "mesh-research",
          encryptedData,
          priority: 0,
          timeoutSecs: 300,
        },
        sessionId: session?.id,
        requestId: taskId,
      });
    },
    [send, session?.id],
  );

  const handlePinMemory = useCallback(
    (memoryId: string) => {
      setMemoryItems((prev) =>
        prev.map((m) =>
          m.id === memoryId ? { ...m, pinned: true, location: "pinned" } : m,
        ),
      );
      send({
        type: "mem.pin.request",
        payload: { key: memoryId, reason: "user-pinned" },
        sessionId: session?.id,
      });
    },
    [send, session?.id],
  );

  const handleBurnMemory = useCallback(
    (memoryId: string) => {
      setMemoryItems((prev) => prev.filter((m) => m.id !== memoryId));
      send({
        type: "mem.burn",
        payload: { key: memoryId },
        sessionId: session?.id,
      });
    },
    [send, session?.id],
  );

  const handleBurnAll = useCallback(() => {
    setMemoryItems([]);
    send({
      type: "mem.burn-all",
      payload: {},
      sessionId: session?.id,
    });
  }, [send, session?.id]);

  // Page transition variants
  const pageVariants = useMemo(
    () => ({
      initial: { opacity: 0, y: 12 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -12 },
    }),
    [],
  );

  return (
    <div className="relative min-h-screen flex flex-col">
      {/* Custom Cursor */}
      <CustomCursor />

      {/* Main Site Navigation Bar */}
      <NavBar currentPage={currentPage} onNavigate={handlePageNavigate} />

      {/* Console Sub-Header - only show when on console page */}
      {currentPage === "console" && (
        <div className="sticky top-16 md:top-[72px] z-40 border-b border-hvme-border bg-hvme-bg-deep/90 backdrop-blur-md">
          <div className="mx-auto max-w-7xl flex items-center justify-between px-6 py-3">
            {/* Console Title */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold tracking-wide text-hvme-text-primary">
                Console
              </span>
              {session && (
                <span className="ml-2 px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest rounded-full border border-hvme-border text-hvme-text-muted">
                  {session.privacyMode}
                </span>
              )}
            </div>

            {/* Console Internal Navigation */}
            <ConsoleNavBar
              currentView={currentView}
              onNavigate={setCurrentView}
              hasSession={session !== null}
            />

            {/* Status */}
            <ConnectionBadge status={status} />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 relative">
        <AnimatePresence mode="wait">
          {/* HOME PAGE */}
          {currentPage === "home" && (
            <motion.div
              key="home"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <SessionLobby
                onStartSession={(mode) => {
                  handleStartSession(mode);
                  setCurrentPage("console");
                }}
                hasActiveSession={session !== null}
                onLearnMore={() => handlePageNavigate("features")}
              />
            </motion.div>
          )}

          {/* FEATURES PAGE */}
          {currentPage === "features" && (
            <motion.div
              key="features"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <FeaturesPage onGetStarted={() => handlePageNavigate("console")} />
            </motion.div>
          )}

          {/* PRICING PAGE */}
          {currentPage === "pricing" && (
            <motion.div
              key="pricing"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <PricingPage onGetStarted={() => handlePageNavigate("console")} />
            </motion.div>
          )}

          {/* ABOUT PAGE */}
          {currentPage === "about" && (
            <motion.div
              key="about"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <AboutPage />
            </motion.div>
          )}

          {/* DOCS PAGE */}
          {currentPage === "docs" && (
            <motion.div
              key="docs"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <DocsPage />
            </motion.div>
          )}

          {/* MARKETPLACE PAGE */}
          {currentPage === "marketplace" && (
            <motion.div
              key="marketplace"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <MarketplacePage onNavigateToConsole={() => handlePageNavigate("console")} />
            </motion.div>
          )}

          {/* CONSOLE PAGE */}
          {currentPage === "console" && (
            <motion.div
              key={`console-${currentView}`}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="h-full"
            >
              {currentView === "lobby" && (
                <SessionLobby
                  onStartSession={handleStartSession}
                  hasActiveSession={session !== null}
                  onLearnMore={() => handlePageNavigate("features")}
                />
              )}

              {currentView === "orchestration" && (
                <div className="mx-auto max-w-7xl px-6 py-8 space-y-8">
                  {/* Task Input */}
                  <TaskInput onSubmit={handleSubmitTask} />

                  {/* Agent Mesh Visualization */}
                  <AgentMesh agents={agents} threads={threads} tasks={tasks} />
                </div>
              )}

              {currentView === "memory" && (
                <div className="mx-auto max-w-7xl px-6 py-8">
                  <MemoryPanel
                    items={memoryItems}
                    onPin={handlePinMemory}
                    onBurn={handleBurnMemory}
                    onBurnAll={handleBurnAll}
                    allowPinning={session?.memoryPolicy.allowPinning ?? false}
                  />
                </div>
              )}

              {currentView === "privacy" && session && (
                <div className="mx-auto max-w-7xl px-6 py-8">
                  <PrivacyAudit
                    session={session}
                    memoryItems={memoryItems}
                    onBurnAll={handleBurnAll}
                    onBurnItem={handleBurnMemory}
                  />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer - show on all pages except console */}
      {currentPage !== "console" && <Footer />}
    </div>
  );
}

// =============================================================================
// Task Input
// =============================================================================

function TaskInput({ onSubmit }: { onSubmit: (description: string) => void }) {
  const [value, setValue] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    setValue("");
  };

  return (
    <form onSubmit={handleSubmit} className="glass-panel rounded-xl p-4">
      <div className="flex gap-3">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Describe a task for the agent mesh..."
          className="
            flex-1 bg-hvme-bg-deep border border-hvme-border rounded-xl
            px-4 py-3 font-body text-sm text-hvme-text-primary placeholder-hvme-text-muted
            focus:outline-none focus:border-hvme-primary/40 focus:ring-1 focus:ring-hvme-primary/20
            transition-all duration-200
          "
        />
        <button
          type="submit"
          disabled={!value.trim()}
          className="
            px-6 py-3 rounded-xl font-body text-sm font-medium transition-all duration-200
            bg-hvme-primary/10 text-hvme-primary border border-hvme-primary/20
            hover:bg-hvme-primary/20 hover:border-hvme-primary/40 hover:shadow-[0_0_20px_rgba(var(--color-hvme-primary-rgb),0.3)]
            disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-hvme-primary/10 disabled:hover:shadow-none
          "
        >
          Submit
        </button>
      </div>
    </form>
  );
}
